(function() {
  const DEFAULT_CROSS_SELL_COPY = {
    headline: "Complete the look",
    matchedBody: "Pair it with this selected style for an easy, pulled-together look.",
    ctaPrefix: "Add matching top",
    secondary: "Skip and view cart",
    addedToCartSuffix: "Added To Cart",
    sizeLabel: "Size",
    colorLabel: "Color",
    soldOutLabel: "Sold out"
  };

  const SWATCH_FALLBACK_COLOR = "#f7f3f1";

  const PRODUCT_CACHE = {};
  const PRELOADED_IMAGES = {};
  const NAMED_COLOR_CACHE = new Map();
  const MODAL_ID = "cross-sell-modal";
  let parsedConfig = null;
  let activeModalState = null;
  let lastTriggerKey = null;
  let lastFocusedElement = null;

  document.addEventListener("variant:added", handleVariantAdded);
  document.addEventListener("shopify:section:load", invalidateCrossSellConfig);
  document.addEventListener("shopify:section:unload", invalidateCrossSellConfig);

  function invalidateCrossSellConfig() {
    parsedConfig = null;
  }

  async function handleVariantAdded(event) {
    const addedVariant = event.detail && event.detail.variant;
    const rule = findCrossSellRule(addedVariant);

    if (!rule) {
      return;
    }

    const triggerKey = [
      rule.triggerProductId,
      addedVariant && addedVariant.id,
      Date.now()
    ].join(":");

    lastTriggerKey = triggerKey;

    try {
      const form = event.target && event.target.matches && event.target.matches("form") ? event.target : null;
      const selectedSourceValue = getSelectedOptionValue(form, rule.sourceOptionName, addedVariant);
      const cart = await fetchCart();

      if (cartContainsProduct(cart, rule.upsellProductId)) {
        return;
      }

      const product = await loadProduct(rule.upsellHandle);
      const resolved = resolveUpsellVariant(product, rule, selectedSourceValue);

      if (!resolved || lastTriggerKey !== triggerKey) {
        return;
      }

      renderCrossSellModal({
        rule,
        product,
        cart,
        triggerProductTitle: getAddedProductTitle(addedVariant, rule),
        selectedSourceValue,
        selectedVariant: resolved.variant,
        selectedColor: resolved.selectedColor,
        colorOptions: resolved.colorOptions,
        upsellVariants: resolved.upsellVariants,
        availableVariants: resolved.availableVariants
      });
    } catch (error) {
      console.warn("Cross-sell modal failed to load", error);
    }
  }

  function findCrossSellRule(addedVariant) {
    const productId = Number(addedVariant && (addedVariant.product_id || addedVariant.productId));

    return getCrossSellRules().find((rule) => rule.triggerProductId === productId);
  }

  function getCrossSellConfig() {
    if (parsedConfig) {
      return parsedConfig;
    }

    const configElement = document.querySelector("[data-cross-sell-config]");

    if (!configElement) {
      parsedConfig = { enabled: false, rules: [], colorSwatches: {} };
      return parsedConfig;
    }

    try {
      const raw = JSON.parse(configElement.textContent || "{}");
      const swatches = {};

      Object.entries(raw.colorSwatches || {}).forEach(([name, value]) => {
        if (name && value) {
          swatches[String(name).trim().toLowerCase()] = String(value).trim();
        }
      });

      parsedConfig = {
        enabled: Boolean(raw.enabled),
        rules: raw.enabled ? normalizeCrossSellRules(raw) : [],
        colorSwatches: swatches,
        freeShipping: raw.freeShipping || null
      };
    } catch (error) {
      console.warn("Cross-sell config failed to parse", error);
      parsedConfig = { enabled: false, rules: [], colorSwatches: {}, freeShipping: null };
    }

    return parsedConfig;
  }

  function getActiveThresholdCents() {
    const freeShipping = getCrossSellConfig().freeShipping;

    if (!freeShipping || !freeShipping.enabled) {
      return null;
    }

    const raw = String(freeShipping.threshold || "").replace(/\s/g, "");

    if (!raw) {
      return null;
    }

    const parts = raw.split(",");
    const currency = String(freeShipping.currency || (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartCurrency) || "USD").toUpperCase();

    if (parts.length === 1 && !parts[0].includes(":")) {
      const single = parseFloat(parts[0]);
      return Number.isFinite(single) ? Math.round(single * 100) : null;
    }

    for (let i = 0; i < parts.length; i += 1) {
      const segments = parts[i].split(":");
      const code = segments[0];
      const amount = segments[1];

      if (code && code.toUpperCase() === currency && amount) {
        const value = parseFloat(amount);
        return Number.isFinite(value) ? Math.round(value * 100) : null;
      }
    }

    return null;
  }

  function evaluateFreeShipping(state) {
    if (!state || !state.cart || !state.selectedVariant) {
      return { state: "hidden" };
    }

    const thresholdCents = getActiveThresholdCents();

    if (!thresholdCents) {
      return { state: "hidden" };
    }

    const cartCents = Number(state.cart.total_price || 0);

    if (cartCents >= thresholdCents) {
      return { state: "hidden" };
    }

    const variantCents = Number(state.selectedVariant.price || 0);
    const projected = cartCents + variantCents;

    if (projected >= thresholdCents) {
      return {
        state: "unlocks",
        remainingCents: thresholdCents - cartCents,
        thresholdCents
      };
    }

    return { state: "hidden" };
  }

  function getCrossSellRules() {
    return getCrossSellConfig().rules;
  }

  function getSwatchHex(colorValue) {
    if (!colorValue) {
      return SWATCH_FALLBACK_COLOR;
    }

    const swatches = getCrossSellConfig().colorSwatches;
    const normalized = String(colorValue).trim().toLowerCase();
    const paletteValue = swatches[normalized];

    if (paletteValue) {
      // Tier 1: explicit hex from merchant palette wins.
      if (paletteValue.startsWith("#")) {
        return paletteValue;
      }

      // Merchant configured an image swatch. The modal does not render image
      // backgrounds yet, so respect the merchant intent and use a neutral
      // tile rather than substituting an unrelated CSS named color.
      // TODO: support background-image swatches like snippets/color-swatch-style.liquid.
      return SWATCH_FALLBACK_COLOR;
    }

    // Tier 2: variant name itself is a CSS named color (Pink, Navy, Olive, etc.).
    const cssHex = resolveCssNamedColor(normalized);

    if (cssHex) {
      return cssHex;
    }

    // Tier 3: neutral tile.
    return SWATCH_FALLBACK_COLOR;
  }

  function resolveCssNamedColor(name) {
    if (!name || typeof document === "undefined") {
      return null;
    }

    if (NAMED_COLOR_CACHE.has(name)) {
      return NAMED_COLOR_CACHE.get(name);
    }

    const candidates = [name, name.replace(/\s+/g, "")];
    let resolved = null;

    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];

      if (!candidate) {
        continue;
      }

      const probe = document.createElement("span");
      probe.style.color = "";
      probe.style.color = candidate;

      // Browsers reject invalid color values, leaving style.color empty.
      if (probe.style.color === "") {
        continue;
      }

      // Reject common non-color tokens that browsers technically accept.
      if (/^(transparent|currentcolor|inherit|initial|unset|revert)$/i.test(candidate)) {
        continue;
      }

      probe.style.position = "absolute";
      probe.style.visibility = "hidden";
      document.body.appendChild(probe);
      const computed = window.getComputedStyle(probe).color;
      document.body.removeChild(probe);

      const match = String(computed).match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);

      if (match) {
        const r = Number(match[1]);
        const g = Number(match[2]);
        const b = Number(match[3]);
        resolved = "#" + [r, g, b].map((channel) => channel.toString(16).padStart(2, "0")).join("");
        break;
      }
    }

    NAMED_COLOR_CACHE.set(name, resolved);
    return resolved;
  }

  function normalizeCrossSellRules(config) {
    const globalCopy = mergeCopy(DEFAULT_CROSS_SELL_COPY, config.copy);
    const rules = Array.isArray(config.rules) ? config.rules : [];

    return rules
      .filter((rule) => rule && rule.enabled !== false && rule.triggerProductId && rule.upsellHandle)
      .map((rule) => {
        const colorOptionName = rule.colorOptionName || globalCopy.colorLabel;
        const defaultColor = rule.defaultColor || rule.defaultOptions && rule.defaultOptions[colorOptionName] || "";

        return {
          triggerProductId: Number(rule.triggerProductId),
          triggerProductTitle: rule.triggerProductTitle || "",
          upsellProductId: Number(rule.upsellProductId || 0),
          upsellHandle: rule.upsellHandle,
          upsellProductTitle: rule.upsellProductTitle || "",
          defaultOptions: {
            ...rule.defaultOptions,
            [colorOptionName]: defaultColor
          },
          sourceOptionName: rule.sourceOptionName || globalCopy.sizeLabel,
          colorOptionName,
          targetOptionName: rule.targetOptionName || globalCopy.sizeLabel,
          showColorPicker: config.showColorPicker !== false && rule.showColorPicker !== false,
          showSoldOutSizes: config.showSoldOutSizes !== false && rule.showSoldOutSizes !== false,
          imageOverride: rule.imageOverride || "",
          copy: mergeCopy(globalCopy, rule.copy)
        };
      });
  }

  function mergeCopy(baseCopy, overrideCopy) {
    const mergedCopy = {
      ...baseCopy
    };

    Object.entries(overrideCopy || {}).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") {
        mergedCopy[key] = value;
      }
    });

    return mergedCopy;
  }

  function getAddedProductTitle(addedVariant, rule) {
    if (rule.triggerProductTitle) {
      return rule.triggerProductTitle;
    }

    const title = addedVariant && addedVariant.title;

    return title && title.includes(" - ") ? title.split(" - ")[0] : title || "Product";
  }

  function getUpsellDisplayTitle(rule, product) {
    if (rule && rule.upsellProductTitle) {
      return rule.upsellProductTitle;
    }

    return product && product.title ? product.title : "";
  }

  function getSelectedOptionValue(form, optionName, addedVariant) {
    const addedVariantOption = getOptionFromAddedVariant(addedVariant, optionName);

    if (addedVariantOption) {
      return addedVariantOption;
    }

    const productVariants = form
      ? document.querySelector(`product-variants[form-id="${escapeAttributeValue(form.id)}"]`)
      : document.querySelector("product-variants");

    if (productVariants && productVariants.product && productVariants.selectedVariant) {
      const optionIndex = getOptionIndex(productVariants.product, optionName);

      if (optionIndex > -1) {
        return productVariants.selectedVariant[`option${optionIndex + 1}`] || null;
      }
    }

    return getOptionFromVariantTitle(addedVariant && addedVariant.title, optionName);
  }

  function getOptionFromAddedVariant(addedVariant, optionName) {
    const normalizedOptionName = normalizeOptionName(optionName);
    const optionsWithValues = addedVariant && addedVariant.options_with_values;

    if (Array.isArray(optionsWithValues)) {
      const matchedOption = optionsWithValues.find((option) => {
        return normalizeOptionName(option && option.name) === normalizedOptionName;
      });

      if (matchedOption && matchedOption.value) {
        return matchedOption.value;
      }
    }

    return getOptionFromVariantTitle(
      addedVariant && (addedVariant.variant_title || addedVariant.title),
      optionName
    );
  }

  function getOptionFromVariantTitle(title, optionName) {
    if (!title || optionName !== "Size") {
      return null;
    }

    const variantTitle = title.includes(" - ") ? title.split(" - ").pop() : title;
    const parts = variantTitle.split("/").map((part) => part.trim()).filter(Boolean);

    return parts.length > 1 ? parts[parts.length - 1] : null;
  }

  function getOptionIndex(product, optionName) {
    const normalizedOptionName = normalizeOptionName(optionName);
    const options = product && product.options ? product.options : [];

    return options.findIndex((option) => {
      const name = typeof option === "string" ? option : option && option.name;
      return normalizeOptionName(name) === normalizedOptionName;
    });
  }

  async function loadProduct(handle) {
    if (!PRODUCT_CACHE[handle]) {
      PRODUCT_CACHE[handle] = fetch(`/products/${handle}.js`, {
        headers: {
          Accept: "application/json"
        }
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load product ${handle}`);
        }

        return response.json();
      });
    }

    return PRODUCT_CACHE[handle];
  }

  function resolveUpsellVariant(product, rule, selectedSourceValue, selectedColor) {
    const colorOptions = getColorOptions(product, rule);
    const preferredColor = selectedColor || rule.defaultOptions && rule.defaultOptions[rule.colorOptionName];
    const fallbackColor = colorOptions.find((color) => color.hasAvailable) || colorOptions[0];
    const activeColor = colorOptions.find((color) => color.value === preferredColor && color.hasAvailable) || fallbackColor;

    if (!activeColor) {
      return null;
    }

    const upsellVariants = product.variants.filter((variant) => {
      return getVariantOptionValue(product, variant, rule.colorOptionName) === activeColor.value;
    });
    const availableVariants = upsellVariants.filter((variant) => variant.available);

    if (availableVariants.length === 0) {
      return null;
    }

    const exactMatch = availableVariants.find((variant) => {
      return getVariantOptionValue(product, variant, rule.targetOptionName) === selectedSourceValue;
    });

    return {
      variant: exactMatch || availableVariants[0],
      selectedColor: activeColor.value,
      colorOptions,
      upsellVariants,
      availableVariants
    };
  }

  function getVariantOptionValue(product, variant, optionName) {
    const optionIndex = getOptionIndex(product, optionName);

    return optionIndex > -1 ? variant[`option${optionIndex + 1}`] : null;
  }

  function getColorOptions(product, rule) {
    const colorValues = getOptionValues(product, rule.colorOptionName);

    return colorValues.map((color) => ({
      value: color,
      hasAvailable: product.variants.some((variant) => {
        return variant.available && getVariantOptionValue(product, variant, rule.colorOptionName) === color;
      })
    }));
  }

  function getOptionValues(product, optionName) {
    const optionIndex = getOptionIndex(product, optionName);
    const values = [];

    if (optionIndex === -1) {
      return values;
    }

    product.variants.forEach((variant) => {
      const value = variant[`option${optionIndex + 1}`];

      if (value && !values.includes(value)) {
        values.push(value);
      }
    });

    return values;
  }

  function renderCrossSellModal(state) {
    teardownCrossSellModal({ restoreFocus: false });

    activeModalState = state;
    lastFocusedElement = document.activeElement;

    const modal = document.createElement("div");
    modal.id = MODAL_ID;
    modal.className = "cross-sell-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "cross-sell-modal-title");
    modal.innerHTML = buildModalHtml(state);

    document.body.appendChild(modal);
    document.body.classList.add("cross-sell-modal-is-open");
    closeCartDrawer();
    bindModalEvents(modal);

    requestAnimationFrame(() => {
      modal.classList.add("is-visible");
      const firstFocusable = getFocusableElements(modal)[0];

      if (firstFocusable) {
        firstFocusable.focus();
      }
    });

    preloadCrossSellImages(state.product);
  }

  function buildModalHtml(state) {
    const { rule, product, triggerProductTitle, colorOptions } = state;
    const display = getCrossSellDisplayData(state);
    const addedToCartText = `${triggerProductTitle} ${rule.copy.addedToCartSuffix}`;
    const upsellTitle = getUpsellDisplayTitle(rule, product);
    const visibleUpsellVariants = getVisibleUpsellVariants(state);
    const freeShippingResult = evaluateFreeShipping(state);
    const ctaLabel = buildCtaLabel(rule, display);

    return `
      <div class="cross-sell-modal__overlay" data-cross-sell-close></div>
      <div class="cross-sell-modal__panel">
        <button type="button" class="cross-sell-modal__close tap-area" data-cross-sell-close aria-label="Close">
          <svg focusable="false" width="14" height="14" class="icon icon--close icon--inline" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
          </svg>
        </button>

        <div class="cross-sell-modal__media">
          <a href="${escapeHtml(display.productUrl)}" class="cross-sell-modal__image-link" tabindex="-1">
            <img src="${escapeHtml(display.image)}" alt="${escapeHtml(upsellTitle)}" class="cross-sell-modal__image" loading="eager" decoding="async">
          </a>
        </div>

        <div class="cross-sell-modal__content">
          <p class="cross-sell-modal__eyebrow heading heading--xxsmall">
            <span class="cross-sell-modal__checkmark" aria-hidden="true">✓</span>
            ${escapeHtml(addedToCartText)}
          </p>
          <h2 id="cross-sell-modal-title" class="cross-sell-modal__title heading h3">${escapeHtml(rule.copy.headline)}</h2>
          <p class="cross-sell-modal__body text--subdued">${escapeHtml(display.bodyCopy)}</p>

          <div class="cross-sell-modal__product">
            <a href="${escapeHtml(display.productUrl)}" class="cross-sell-modal__product-title heading heading--small">${escapeHtml(upsellTitle)}</a>
            <div class="cross-sell-modal__price-list price-list">
              ${buildCrossSellPriceHtml(display)}
            </div>
          </div>

          ${rule.showColorPicker === false ? "" : `
            <div class="cross-sell-modal__color-picker">
              <p class="cross-sell-modal__option-heading">${escapeHtml(rule.copy.colorLabel)}: <span>${escapeHtml(display.selectedColorValue || "")}</span></p>
              <div class="cross-sell-modal__colors" role="radiogroup" aria-label="${escapeHtml(rule.copy.colorLabel)}">
                ${colorOptions.map((color) => buildColorButton(color, display.selectedColorValue, rule)).join("")}
              </div>
            </div>
          `}

          <div class="cross-sell-modal__size-picker">
            <p class="cross-sell-modal__option-heading">${escapeHtml(rule.copy.sizeLabel)}: <span>${escapeHtml(display.selectedSize || "")}</span></p>
            <div class="cross-sell-modal__sizes" role="radiogroup" aria-label="${escapeHtml(rule.copy.sizeLabel)}">
              ${visibleUpsellVariants.map((variant) => buildSizeButton(product, rule, variant, state.selectedVariant.id)).join("")}
            </div>
          </div>

          <div class="cross-sell-modal__actions">
            <div class="cross-sell-modal__actions-primary">
              ${buildFreeShippingPillHtml(state, freeShippingResult)}
              <button type="button" class="cross-sell-modal__add-button button button--primary button--full" data-cross-sell-add>
                ${escapeHtml(ctaLabel)}
              </button>
            </div>
            <button type="button" class="cross-sell-modal__secondary link text--subdued" data-cross-sell-view-cart>
              ${escapeHtml(rule.copy.secondary)}
            </button>
            <p class="cross-sell-modal__error" data-cross-sell-error hidden></p>
          </div>
        </div>
      </div>
    `;
  }

  function buildCtaLabel(rule, display) {
    return `${rule.copy.ctaPrefix} - ${display.price}`;
  }

  function buildFreeShippingPillHtml(state, result) {
    const isUnlock = result.state === "unlocks";

    return `
      <p
        class="cross-sell-modal__free-shipping${isUnlock ? " is-visible" : ""}"
        data-cross-sell-free-shipping
        aria-live="polite"
        aria-hidden="${isUnlock ? "false" : "true"}"
      >
        <span class="cross-sell-modal__free-shipping-emoji" aria-hidden="true">🎉</span>
        <span class="cross-sell-modal__free-shipping-text">${isUnlock ? formatFreeShippingCopy(getFreeShippingEyebrowCopy()) : ""}</span>
      </p>
    `;
  }

  function getFreeShippingEyebrowCopy() {
    const config = getCrossSellConfig().freeShipping;

    return config && config.copy && config.copy.unlockEyebrow
      ? String(config.copy.unlockEyebrow).trim()
      : "Adding this unlocks FREE shipping!";
  }

  function formatFreeShippingCopy(text) {
    if (!text) {
      return "";
    }

    return escapeHtml(text).replace(
      /(FREE\s+SHIPPING)/i,
      '<span class="cross-sell-modal__free-shipping-emphasis">$1</span>'
    );
  }

  function updateCrossSellModal(state, options) {
    const modal = document.getElementById(MODAL_ID);

    if (!modal) {
      renderCrossSellModal(state);
      return;
    }

    activeModalState = state;

    const display = getCrossSellDisplayData(state);
    const imageLink = modal.querySelector(".cross-sell-modal__image-link");
    const image = modal.querySelector(".cross-sell-modal__image");
    const productTitle = modal.querySelector(".cross-sell-modal__product-title");
    const body = modal.querySelector(".cross-sell-modal__body");
    const priceList = modal.querySelector(".cross-sell-modal__price-list");
    const colorHeading = modal.querySelector(".cross-sell-modal__color-picker .cross-sell-modal__option-heading span");
    const sizeHeading = modal.querySelector(".cross-sell-modal__size-picker .cross-sell-modal__option-heading span");
    const sizes = modal.querySelector(".cross-sell-modal__sizes");
    const addButton = modal.querySelector("[data-cross-sell-add]");

    if (imageLink) {
      imageLink.href = display.productUrl;
    }

    if (image && display.image && image.getAttribute("src") !== display.image) {
      image.src = display.image;
    }

    if (image) {
      image.alt = getUpsellDisplayTitle(state.rule, state.product);
    }

    if (productTitle) {
      productTitle.href = display.productUrl;
    }

    if (body) {
      body.textContent = display.bodyCopy;
    }

    if (priceList) {
      priceList.innerHTML = buildCrossSellPriceHtml(display);
    }

    if (colorHeading) {
      colorHeading.textContent = display.selectedColorValue || "";
    }

    if (sizeHeading) {
      sizeHeading.textContent = display.selectedSize || "";
    }

    updateColorButtons(modal, display.selectedColorValue);

    if (sizes && options && options.refreshSizes) {
      sizes.innerHTML = getVisibleUpsellVariants(state).map((variant) => buildSizeButton(state.product, state.rule, variant, state.selectedVariant.id)).join("");
    } else {
      updateSizeButtons(modal, state.selectedVariant.id);
    }

    if (addButton) {
      addButton.textContent = buildCtaLabel(state.rule, display);
    }

    updateFreeShippingPill(modal, evaluateFreeShipping(state));
  }

  function updateFreeShippingPill(modal, result) {
    const pill = modal.querySelector("[data-cross-sell-free-shipping]");

    if (!pill) {
      return;
    }

    const isUnlock = result.state === "unlocks";
    const textNode = pill.querySelector(".cross-sell-modal__free-shipping-text");

    if (isUnlock) {
      if (textNode) {
        textNode.innerHTML = formatFreeShippingCopy(getFreeShippingEyebrowCopy());
      }

      pill.classList.add("is-visible");
      pill.setAttribute("aria-hidden", "false");
    } else {
      pill.classList.remove("is-visible");
      pill.setAttribute("aria-hidden", "true");

      if (textNode) {
        textNode.innerHTML = "";
      }
    }
  }

  function getCrossSellDisplayData(state) {
    const { rule, product, selectedVariant, selectedColor } = state;
    const image = rule.imageOverride || getVariantImage(selectedVariant) || normalizeImageUrl(product.featured_image);
    const selectedSize = getVariantOptionValue(product, selectedVariant, rule.targetOptionName);
    const selectedColorValue = selectedColor || getVariantOptionValue(product, selectedVariant, rule.colorOptionName);
    const price = formatCrossSellMoney(selectedVariant.price);
    const compareAtPrice = selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price
      ? formatCrossSellMoney(selectedVariant.compare_at_price)
      : "";
    const discountLabel = getDiscountLabel(selectedVariant);
    const bodyCopy = rule.copy.matchedBody;
    const productUrl = `${product.url || `/products/${rule.upsellHandle}`}?variant=${selectedVariant.id}`;

    return {
      image,
      selectedSize,
      selectedColorValue,
      price,
      compareAtPrice,
      discountLabel,
      bodyCopy,
      productUrl
    };
  }

  function getVisibleUpsellVariants(state) {
    if (state.rule.showSoldOutSizes === false) {
      return state.upsellVariants.filter((variant) => variant.available);
    }

    return state.upsellVariants;
  }

  function buildCrossSellPriceHtml(display) {
    return `
      <span class="price price--highlight">${escapeHtml(display.price)}</span>
      ${display.compareAtPrice ? `<span class="price price--compare">${escapeHtml(display.compareAtPrice)}</span>` : ""}
      ${display.discountLabel ? `<span class="cross-sell-modal__discount-label">${escapeHtml(display.discountLabel)}</span>` : ""}
    `;
  }

  function updateColorButtons(modal, selectedColorValue) {
    Array.from(modal.querySelectorAll("[data-cross-sell-color]")).forEach((button) => {
      const isSelected = button.getAttribute("data-cross-sell-color") === selectedColorValue;

      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-checked", isSelected ? "true" : "false");
    });
  }

  function updateSizeButtons(modal, selectedVariantId) {
    Array.from(modal.querySelectorAll("[data-cross-sell-variant-id]")).forEach((button) => {
      const isSelected = Number(button.getAttribute("data-cross-sell-variant-id")) === Number(selectedVariantId);

      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-checked", isSelected ? "true" : "false");
    });
  }

  function buildColorButton(color, selectedColor, rule) {
    const isSelected = color.value === selectedColor;
    const swatchColor = getSwatchHex(color.value);

    return `
      <button
        type="button"
        class="cross-sell-modal__color ${isSelected ? "is-selected" : ""} ${color.hasAvailable ? "" : "is-sold-out"}"
        data-cross-sell-color="${escapeHtml(color.value)}"
        role="radio"
        aria-checked="${isSelected ? "true" : "false"}"
        aria-disabled="${color.hasAvailable ? "false" : "true"}"
        title="${escapeHtml(color.hasAvailable ? color.value : `${color.value} - ${rule.copy.soldOutLabel}`)}"
        ${color.hasAvailable ? "" : "disabled"}
      >
        <span class="cross-sell-modal__color-swatch" style="--cross-sell-swatch-color: ${escapeHtml(swatchColor)}"></span>
        <span class="visually-hidden">${escapeHtml(color.value)}</span>
      </button>
    `;
  }

  function buildSizeButton(product, rule, variant, selectedVariantId) {
    const size = getVariantOptionValue(product, variant, rule.targetOptionName);
    const isSelected = variant.id === selectedVariantId;
    const isAvailable = variant.available;

    return `
      <button
        type="button"
        class="cross-sell-modal__size ${isSelected ? "is-selected" : ""} ${isAvailable ? "" : "is-sold-out"}"
        data-cross-sell-variant-id="${variant.id}"
        role="radio"
        aria-checked="${isSelected ? "true" : "false"}"
        aria-disabled="${isAvailable ? "false" : "true"}"
        title="${isAvailable ? "" : escapeHtml(rule.copy.soldOutLabel)}"
        ${isAvailable ? "" : "disabled"}
      >
        ${escapeHtml(size)}
      </button>
    `;
  }

  function bindModalEvents(modal) {
    modal.addEventListener("click", handleModalClick);
    document.addEventListener("keydown", handleModalKeydown);
  }

  async function handleModalClick(event) {
    const modal = document.getElementById(MODAL_ID);

    if (!modal) {
      return;
    }

    const colorButton = event.target.closest("[data-cross-sell-color]");

    if (colorButton) {
      selectUpsellColor(colorButton.getAttribute("data-cross-sell-color"));
      return;
    }

    const sizeButton = event.target.closest("[data-cross-sell-variant-id]");

    if (sizeButton) {
      selectUpsellVariant(Number(sizeButton.getAttribute("data-cross-sell-variant-id")));
      return;
    }

    if (event.target.closest("[data-cross-sell-close]")) {
      closeCrossSellModal();
      return;
    }

    if (event.target.closest("[data-cross-sell-view-cart]")) {
      closeCrossSellModal();
      openCartDrawer();
      return;
    }

    if (event.target.closest("[data-cross-sell-add]")) {
      await handleAddUpsell();
    }
  }

  function selectUpsellVariant(variantId) {
    if (!activeModalState) {
      return;
    }

    const variant = activeModalState.availableVariants.find((availableVariant) => availableVariant.id === variantId);

    if (!variant) {
      return;
    }

    updateCrossSellModal({
      ...activeModalState,
      selectedVariant: variant
    });
  }

  function selectUpsellColor(color) {
    if (!activeModalState) {
      return;
    }

    const resolved = resolveUpsellVariant(
      activeModalState.product,
      activeModalState.rule,
      activeModalState.selectedSourceValue,
      color
    );

    if (!resolved) {
      return;
    }

    updateCrossSellModal({
      ...activeModalState,
      selectedColor: resolved.selectedColor,
      colorOptions: resolved.colorOptions,
      selectedVariant: resolved.variant,
      upsellVariants: resolved.upsellVariants,
      availableVariants: resolved.availableVariants
    }, {
      refreshSizes: true
    });
  }

  async function handleAddUpsell() {
    if (!activeModalState) {
      return;
    }

    const modal = document.getElementById(MODAL_ID);
    const addButton = modal && modal.querySelector("[data-cross-sell-add]");
    const errorElement = modal && modal.querySelector("[data-cross-sell-error]");

    if (addButton) {
      addButton.setAttribute("aria-busy", "true");
      addButton.disabled = true;
    }

    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = "";
    }

    try {
      const cart = await addUpsellVariant(activeModalState.selectedVariant.id);
      closeCrossSellModal();
      refreshCart(cart);
      openCartDrawer();
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = "Sorry, we could not add this item. Please try again.";
        errorElement.hidden = false;
      }

      if (addButton) {
        addButton.removeAttribute("aria-busy");
        addButton.disabled = false;
      }
    }
  }

  async function addUpsellVariant(variantId) {
    const response = await fetch(window.themeVariables.routes.cartAddUrl + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (!response.ok) {
      throw new Error("Unable to add upsell variant");
    }

    return fetchCart();
  }

  async function fetchCart() {
    const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("Unable to load cart");
    }

    return response.json();
  }

  function refreshCart(cart) {
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
      bubbles: true,
      detail: {
        cart
      }
    }));

    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: {
        cart
      }
    }));
  }

  function cartContainsProduct(cart, productId) {
    return Boolean(cart && cart.items && cart.items.some((item) => Number(item.product_id) === Number(productId)));
  }

  function closeCrossSellModal() {
    const modal = document.getElementById(MODAL_ID);

    if (!modal) {
      teardownCrossSellModal({ restoreFocus: true });
      return;
    }

    modal.classList.remove("is-visible");

    setTimeout(() => {
      teardownCrossSellModal({ restoreFocus: true });
    }, 250);
  }

  function teardownCrossSellModal(options) {
    const modal = document.getElementById(MODAL_ID);
    const shouldRestoreFocus = options && options.restoreFocus;

    if (modal) {
      modal.remove();
    }

    document.removeEventListener("keydown", handleModalKeydown);
    document.body.classList.remove("cross-sell-modal-is-open");
    activeModalState = null;

    if (shouldRestoreFocus && lastFocusedElement && typeof lastFocusedElement.focus === "function") {
      lastFocusedElement.focus();
    }

    if (shouldRestoreFocus) {
      lastFocusedElement = null;
    }
  }

  function handleModalKeydown(event) {
    const modal = document.getElementById(MODAL_ID);

    if (!modal) {
      return;
    }

    if (event.key === "Escape") {
      closeCrossSellModal();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    const focusableElements = getFocusableElements(modal);

    if (focusableElements.length === 0) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    ));
  }

  function openCartDrawer() {
    const miniCart = document.getElementById("mini-cart");

    if (miniCart) {
      miniCart.open = true;
    } else {
      window.location.href = window.themeVariables.routes.cartUrl;
    }
  }

  function closeCartDrawer() {
    const miniCart = document.getElementById("mini-cart");

    if (miniCart) {
      miniCart.open = false;
    }
  }

  function normalizeImageUrl(image) {
    if (!image) {
      return "";
    }

    const src = typeof image === "string" ? image : image.src;

    return src && src.startsWith("//") ? `${window.location.protocol}${src}` : src;
  }

  function getVariantImage(variant) {
    return normalizeImageUrl(variant && variant.featured_image);
  }

  function preloadCrossSellImages(product) {
    const preload = () => {
      if (!product || !product.variants) {
        return;
      }

      product.variants.forEach((variant) => {
        const url = getVariantImage(variant);

        if (!url || PRELOADED_IMAGES[url]) {
          return;
        }

        PRELOADED_IMAGES[url] = new Image();
        PRELOADED_IMAGES[url].src = url;
      });
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(preload);
    } else {
      setTimeout(preload, 0);
    }
  }

  function getDiscountLabel(variant) {
    if (!variant || !variant.compare_at_price || variant.compare_at_price <= variant.price) {
      return "";
    }

    const discount = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);

    return discount > 0 ? `${discount}% OFF` : "";
  }

  function normalizeOptionName(value) {
    return String(value || "").trim().toLowerCase();
  }

  function escapeAttributeValue(value) {
    return String(value || "").replace(/"/g, '\\"');
  }

  function escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  function formatCrossSellMoney(cents) {
    const amount = Number(cents || 0) / 100;

    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: window.themeVariables.settings.cartCurrency || "USD",
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
  }
})();
